import { commonUtils, SUBSCRIPTION_TYPE } from '@/helpers/commonUtils'
import { BaseVm } from '@/mobxTools'
import { buyVipService } from '@/services/buyVipService'
import { userService } from '@/services/userService'
import { observable } from 'mobx'
import type { PayVipUpgradeDialogProps } from './PayVipUpgradeDialog.tsx'

export class PayVipUpgradeDialogVm extends BaseVm<PayVipUpgradeDialogProps> {
  props!: PayVipUpgradeDialogProps

  @observable visible = false
  @observable currentTabKey: 'wx_pub_qr' | 'alipay_qr' = 'alipay_qr'
  @observable qrUrl?: string
  @observable qrErr = false
  @observable qrLoading = false
  @observable price = 0

  private timer?: any
  private sessionId?: string
  private pollingSessionId?: string

  public onViewDestroy() {
    this.clearTimeout()
  }

  public onViewCreate(): void {
    if (!this.props.open || !this.props.planUpgrade) {
      console.log('onViewCreate: clearTimeout')
      this.clearTimeout()
      return
    }
    this.genOrderQrCode()
  }

  public async genOrderQrCode() {
    if (!userService.isLogin) {
      console.error('未找到用户登录态')
      return
    }

    const sessionId = commonUtils.genId()
    this.sessionId = sessionId
    const selectPlan = this.props.planUpgrade
    if (!selectPlan) {
      return
    }

    // 生成新的轮询会话ID
    this.clearTimeout()

    const isSubscription = SUBSCRIPTION_TYPE.includes(selectPlan.upgradeTimeType)
    try {
      this.qrLoading = true
      const { qr, orderId, money } = await buyVipService.createOrder({
        action: userService.userData?.versionData.version === 'free' ? 'buy' : 'renew',
        chargeChannel:
          isSubscription && this.currentTabKey === 'alipay_qr'
            ? 'alipay_agreement_qr'
            : this.currentTabKey,
        planId: selectPlan.planId,
        upgradeId: selectPlan.upgradeId,
        version: selectPlan.upgradeVersion,
        source: this.props.source || 'vip_upgrade',
      })
      if (this.sessionId !== sessionId) {
        return
      }

      if (this.props.from === 'pricingPage') {
        userService.postHuiyiRecord({
          carrier: 'web-定价页',
          module: '定价页',
          function: `${
            selectPlan.upgradeVersion.charAt(0).toUpperCase() + selectPlan.upgradeVersion.slice(1)
          }付费弹窗-补差价升级`,
          position: `定价页-${
            selectPlan.upgradeVersion.charAt(0).toUpperCase() + selectPlan.upgradeVersion.slice(1)
          }付费弹窗-补差价升级`,
          action: '展现',
          element: '弹窗',
          remark: {
            feature: this.props.feature,
            orderId,
          },
        })
      }

      this.price = money / 100
      this.qrUrl = qr

      this.pollingSessionId = commonUtils.genId()
      this.pollingOrderStatus(orderId, true)
    } catch (error) {
      console.log(error)

      // 常规模式：更新 qrErr
      this.qrErr = true
    } finally {
      this.qrLoading = false
    }
  }

  private async pollingOrderStatus(orderId: string, force = false, cnt = 0) {
    // 检查组件是否已关闭
    if (!this.props.open) {
      return
    }
    if (force) {
      try {
        const { status } = await buyVipService.pollOrderStatus(orderId)

        // 异步请求完成后检查会话是否仍然有效
        if (!this.props.open || !this.pollingSessionId) {
          return
        }

        if (status === 'completed') {
          /** 停止轮询 */
          this.clearTimeout()
          try {
            commonUtils.sendExtensionMessage('markPaid', {})
            await userService.updateUserInfo()
          } catch (error) {}
          if (this.props.paySuccess) {
            this.props.paySuccess(this.props.planUpgrade.upgradeTimeType === 'year' ? true : false)
          }
          setTimeout(() => {
            if (this.props.onClose) {
              this.props.onClose()
            }
          })
          return
        }
        if (status === 'canceled' || status === 'refunded') {
          /** 停止轮询 */
          return
        }
        if (status === 'created') {
          /** 取消上一次轮询 */
          this.clearTimer()
          // 保存当前轮询会话ID，用于回调中验证
          const currentPollingSessionId = this.pollingSessionId
          this.timer = setTimeout(() => {
            // 在回调中检查组件状态和会话有效性
            if (this.props.open && this.pollingSessionId === currentPollingSessionId) {
              this.pollingOrderStatus(orderId, true, 0)
            }
          }, 1000)
        }
      } catch (error) {
        if (cnt > 5) {
          this.clearTimeout()
          this.qrErr = true
          /** 重试结束，停止轮询 */
          return
        }
        // 在错误重试前检查组件状态和会话有效性
        if (this.props.open && this.pollingSessionId) {
          this.pollingOrderStatus(orderId, true, cnt + 1)
        }
      }
    }
  }

  private clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = undefined
    }
  }

  private clearTimeout() {
    this.clearTimer()
    // 清除轮询会话ID，确保所有轮询停止
    this.pollingSessionId = undefined
  }
}
